import type { TransformedCart } from "../../cart/types/cartApiTypes";
import type { TransformedCheckoutData } from "../types/checkoutApiTypes";

// Define the shape of the labels object we expect
export interface AgreementLabels {
  customerType: string;
  individual: string;
  corporate: string;
  companyTitle: string;
  tckn: string;
  vkn: string;
  notSpecified: string;
  authorized: string;
  orderNumberPlaceholder: string;
  paymentMethod: string; // stand-alone label
  table: {
    product: string;
    quantity: string;
    total: string;
    subtotal: string;
    discount: string;
    shipping: string;
    fees: string;
    tax: string;
    grandTotal: string;
    freeShipping: string;
    localPickup: string;
    paymentMethod: string; // table row label
  };
}

/**
 * Generates the HTML rows for the table footer (Totals).
 * Uses strictly formatted strings from the API totals object.
 * Logic matches AgreementGeneratorService.php.
 */
const generateTotalsFooter = (
  cart: TransformedCart,
  checkoutData: TransformedCheckoutData,
  labels: AgreementLabels["table"],
): string => {
  const { totals } = cart;
  let rows = "";

  // 1. Ara Toplam (Item Subtotal)
  rows += `
    <tr>
      <td colspan="2" style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${labels.subtotal}</strong></td>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${totals.totalItems}</td>
    </tr>
  `;

  // 2. Tax (Vergiler)
  if (totals.hasTax) {
    rows += `
      <tr>
        <td colspan="2" style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${labels.tax}</strong></td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${totals.totalTax}</td>
      </tr>
    `;
  }

  // 3. Shipping (Gönderim)
  if (cart.hasCalculatedShipping) {
    let shippingLabel = totals.totalShipping;
    if (totals.shippingStatus === "free") shippingLabel = labels.freeShipping;
    if (totals.shippingStatus === "pickup") shippingLabel = labels.localPickup;

    rows += `
      <tr>
        <td colspan="2" style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${labels.shipping}</strong></td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${shippingLabel}</td>
      </tr>
    `;
  }

  // 4. Fees (Optional)
  if (totals.hasFees) {
    rows += `
      <tr>
        <td colspan="2" style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${labels.fees}</strong></td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${totals.totalFees}</td>
      </tr>
    `;
  }

  // 5. Discount (İndirim)
  if (totals.hasDiscount) {
    rows += `
      <tr>
        <td colspan="2" style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${labels.discount}</strong></td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right; color: #16a34a;">-${totals.totalDiscount}</td>
      </tr>
    `;
  }

  // 6. Payment Method Row
  const selectedMethod = checkoutData.availablePaymentMethods?.find(
    (m) => m.id === checkoutData.paymentMethod,
  );
  const methodTitle = selectedMethod
    ? selectedMethod.title
    : checkoutData.paymentMethod;

  rows += `
    <tr>
      <td colspan="2" style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${labels.paymentMethod}</strong></td>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${methodTitle}</td>
    </tr>
  `;

  // 7. Grand Total
  rows += `
    <tr>
      <td colspan="2" style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${labels.grandTotal}</strong></td>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: right;"><strong>${totals.totalPrice}</strong></td>
    </tr>
  `;

  return rows;
};

/**
 * Generates an HTML table row for a single cart item.
 */
const generateItemRow = (item: TransformedCart["items"][0]): string => {
  return `
    <tr>
      <td style="border: 1px solid #ddd; padding: 8px;">
        ${item.name} <br/>
        <small>${item.variation
          .map((v) => `${v.attribute}: ${v.value}`)
          .join(", ")}</small>
      </td>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">
        ${item.quantity}
      </td>
      <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">
        ${item.formattedCalculatedLineTotal}
      </td>
    </tr>
  `;
};

/**
 * Generates the full HTML table for ordered items.
 */
const generateItemsTable = (
  cart: TransformedCart,
  checkoutData: TransformedCheckoutData,
  labels: AgreementLabels["table"],
): string => {
  const itemRows = cart.items.map(generateItemRow).join("");
  const footerRows = generateTotalsFooter(cart, checkoutData, labels);

  return `
    <table style="width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 14px;">
      <thead>
        <tr style="background-color: #f8f8f8;">
          <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">${labels.product}</th>
          <th style="border: 1px solid #ddd; padding: 8px; text-align: center;">${labels.quantity}</th>
          <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">${labels.total}</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
      </tbody>
      <tfoot>
        ${footerRows}
      </tfoot>
    </table>
  `;
};

/**
 * Pure function to generate the final agreement HTML string.
 * Replaces placeholders in the template with actual cart/checkout data using localized labels.
 */
export const generateAgreementHtml = (
  template: string,
  cart: TransformedCart,
  checkoutData: TransformedCheckoutData,
  labels: AgreementLabels,
): string => {
  const { billingAddress } = checkoutData;
  const sellerInfo = checkoutData.legalAgreements?.sellerInfo;

  // Feature Flag: Check if TCKN is enabled globally
  const enableTckn = window.commonVariables?.enableTckn ?? true;

  if (!template) return "";

  let processedTemplate = template;

  // --- 1. Customer Type Logic (Identity Info) ---
  const isCorporate = billingAddress.customerType === "corporate";
  const isTurkey = billingAddress.country === "TR";

  let buyerName = `${billingAddress.firstName} ${billingAddress.lastName}`;
  if (isCorporate && billingAddress.company) {
    buyerName = `${billingAddress.company} (${labels.authorized}: ${buyerName})`;
  }

  // --- Build Smart Identity Block ---
  let identityInfo = "";

  if (isCorporate) {
    identityInfo = `<strong>${labels.customerType}:</strong> ${labels.corporate}<br>`;
    identityInfo += `<strong>${labels.companyTitle}:</strong> ${
      billingAddress.company || labels.notSpecified
    }`;

    // Only show VKN if the country is Turkey
    if (isTurkey) {
      const vknDisplay = billingAddress.vkn || labels.notSpecified;
      identityInfo += `<br><strong>${labels.vkn}:</strong> ${vknDisplay}`;
    }
  } else {
    identityInfo = `<strong>${labels.customerType}:</strong> ${labels.individual}`;

    // Only show TCKN if the country is Turkey AND TCKN is enabled
    if (isTurkey && enableTckn) {
      const tcknDisplay = billingAddress.tckn || labels.notSpecified;
      identityInfo += `<br><strong>${labels.tckn}:</strong> ${tcknDisplay}`;
    }
  }

  const buyerPhone = billingAddress.phone || "";
  const buyerEmail = billingAddress.email || "";

  // --- 2. Address Construction (Updated to match Backend) ---
  const allCountryStates = window.checkoutFlowVariables?.countryStates || {};
  const allowedCountries = window.checkoutFlowVariables?.allowedCountries || {};

  // Resolve State Name
  const statesForCountry = allCountryStates[billingAddress.country] || {};
  const stateName = billingAddress.state
    ? statesForCountry[billingAddress.state] || billingAddress.state
    : "";

  // Resolve Country Name
  const countryName =
    allowedCountries[billingAddress.country] || billingAddress.country;

  const addressParts: string[] = [];

  if (billingAddress.address1) addressParts.push(billingAddress.address1);
  if (billingAddress.address2) addressParts.push(billingAddress.address2);

  // Line 3: Postcode City / State
  const line3Parts = [billingAddress.postcode, billingAddress.city].filter(
    Boolean,
  );
  let line3Str = line3Parts.join(" ");

  if (stateName) {
    line3Str += ` / ${stateName}`;
  }
  if (line3Str) addressParts.push(line3Str);

  // Line 4: Country
  if (countryName) addressParts.push(countryName);

  const addressString = addressParts.join("<br>");

  // --- 3. Resolve Payment Method Title ---
  const selectedMethod = checkoutData.availablePaymentMethods?.find(
    (m) => m.id === checkoutData.paymentMethod,
  );
  const methodTitle = selectedMethod
    ? selectedMethod.title
    : checkoutData.paymentMethod;

  // --- 4. Generate Replacements ---
  const orderItemsTable = generateItemsTable(cart, checkoutData, labels.table);

  // Helper to extract domain from siteUrl
  const siteUrl = window.commonVariables?.siteUrl || "";
  let siteDomain = "";
  try {
    siteDomain = new URL(siteUrl).hostname;
  } catch (e) {
    siteDomain = siteUrl;
  }

  const replacements: Record<string, string> = {
    "{{buyer_name}}": buyerName,
    "{{buyer_address}}": addressString,
    "{{buyer_phone}}": buyerPhone,
    "{{buyer_email}}": buyerEmail,
    "{{buyer_identity_block}}": identityInfo,
    "{{customer_type}}": isCorporate ? labels.corporate : labels.individual,
    "{{company_name}}": isCorporate ? billingAddress.company || "" : "",

    // For raw placeholders, empty string if not Turkey or wrong type or DISABLED
    "{{tckn}}":
      !isCorporate && isTurkey && enableTckn ? billingAddress.tckn || "" : "",
    "{{vkn}}": isCorporate && isTurkey ? billingAddress.vkn || "" : "",

    "{{seller_name}}": sellerInfo?.name || "",
    "{{seller_address}}": sellerInfo?.address || "",
    "{{seller_phone}}": sellerInfo?.phone || "",
    "{{seller_email}}": sellerInfo?.email || "",
    "{{seller_mersis}}": sellerInfo?.mersis || "",
    "{{site_domain}}": siteDomain,

    "{{order_date}}": new Date().toLocaleDateString(
      window.commonVariables?.language.current === "tr" ? "tr-TR" : "en-US",
    ),
    "{{order_number}}": labels.orderNumberPlaceholder,
    "{{total_amount}}": cart.totals.totalPrice,
    "{{payment_method}}": methodTitle,
    "{{order_items_table}}": orderItemsTable,
  };

  Object.entries(replacements).forEach(([placeholder, value]) => {
    const regex = new RegExp(placeholder, "g");
    processedTemplate = processedTemplate.replace(regex, value);
  });

  return processedTemplate;
};
